import {
  CREATE_ENTITY_APPLICATION_ONLY_URL,
  DEFAULT_NEW_CASE_FORM,
  axios,
  clear_entity_profile_list_cache,
  get_entity_type_name,
  get_request_config,
  normalize_text,
  to_number,
} from './company_cases_helpers';
import { prepareXVerdictOnboarding } from '@/services/xverdict_entity_config_service';

function project_payload(project_id) {
  return project_id ? { project_id: Number(project_id) } : {};
}

function creation_error_message(error) {
  return error?.response?.data?.message
    || error?.response?.data?.detail
    || error?.message
    || 'Failed to create case.';
}

function onboarding_error_message(error) {
  return error?.response?.data?.message
    || error?.message
    || 'The onboarding preview could not be prepared.';
}

function selected_entity_type_id(component) {
  return to_number(component.new_case_form.entity_type_id);
}

function selected_case_type(component) {
  return component.case_types.find(
    (item) => Number(item?.id) === Number(selected_entity_type_id(component))
  ) || null;
}

function preview_form_ids(component) {
  return (Array.isArray(component.onboarding_preview_forms) ? component.onboarding_preview_forms : [])
    .map((form) => to_number(form?.form_id || form?.id))
    .filter(Boolean);
}

function selected_form_ids(component) {
  const case_type_form_ids = Array.isArray(selected_case_type(component)?.form_ids)
    ? selected_case_type(component).form_ids.map((form_id) => to_number(form_id)).filter(Boolean)
    : [];

  if (case_type_form_ids.length) {
    return case_type_form_ids;
  }

  return preview_form_ids(component);
}

function is_email_source(source_channel = '') {
  return normalize_text(source_channel).toLowerCase() === 'email';
}

function is_valid_email(value = '') {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalize_text(value));
}

function ensure_successful_preview(response = {}) {
  if (response?.status === 'error') {
    throw new Error([
      response.message,
      'The onboarding preview could not be prepared.',
    ].find(Boolean));
  }
}

function apply_onboarding_preview(component, response, entity_type_id) {
  if (selected_entity_type_id(component) !== entity_type_id) return;
  ensure_successful_preview(response);
  component.onboarding_preview = response?.data ?? null;
}

function apply_onboarding_error(component, error, entity_type_id) {
  if (selected_entity_type_id(component) === entity_type_id) {
    component.onboarding_preview_error = onboarding_error_message(error);
  }
}

function finish_onboarding_preview(component, entity_type_id) {
  if (selected_entity_type_id(component) === entity_type_id) {
    component.onboarding_preview_loading = false;
  }
}

const CASE_VALIDATIONS = [
  {
    invalid: (context) => !context.entity_type_id,
    error: 'Select a domain before creating the case.',
  },
  {
    invalid: (context) => !context.description && !context.requester,
    error: 'Enter a requester or short description before creating the case.',
  },
  {
    invalid: (context) => context.is_email_source && !context.user_email,
    error: 'Enter an email address when Email is selected as the source.',
  },
  {
    invalid: (context) => context.user_email && !is_valid_email(context.user_email),
    error: 'Enter a valid user email address.',
  },
];

function require_onboarding_ids(create_data = {}) {
  const entity_profile_id = to_number([
    create_data?.entity_profile_id,
    create_data?.data?.entity_profile_id,
  ].find(Boolean));
  const application_id = to_number([
    create_data?.application_id,
    create_data?.data?.application_id,
  ].find(Boolean));
  if (!entity_profile_id || !application_id) {
    throw new Error('The case was created but its onboarding identifiers were not returned.');
  }
  return { entity_profile_id, application_id };
}

function created_case_code(create_data = {}, application_id) {
  return normalize_text(
    create_data?.reference_number
    || create_data?.data?.reference_number
    || create_data?.case_code
    || create_data?.data?.case_code
    || `C-${application_id}`
  );
}

export const company_cases_creation_methods = {
  open_new_case_modal() {
    this.reset_new_case_onboarding();
    if (!this.new_case_form.entity_type_id && this.new_case_domain_options[0]?.id) {
      this.new_case_form.entity_type_id = String(this.new_case_domain_options[0].id);
    }
    this.show_new_case_modal = true;
    this.prepare_new_case_onboarding();
  },
  close_new_case_modal() {
    if (this.submitting_new_case) return;

    this.show_new_case_modal = false;
    this.new_case_form = { ...DEFAULT_NEW_CASE_FORM };
    this.reset_new_case_onboarding();
    if (this.new_case_domain_options[0]?.id) {
      this.new_case_form.entity_type_id = String(this.new_case_domain_options[0].id);
    }
  },
  reset_new_case_onboarding() {
    this.new_case_error = '';
    this.onboarding_preview = null;
    this.onboarding_preview_loading = false;
    this.onboarding_preview_error = '';
    this.onboarding_execution = null;
    this.onboarding_status = null;
    this.created_case = null;
  },
  async prepare_new_case_onboarding() {
    const entity_type_id = to_number(this.new_case_form.entity_type_id);
    this.onboarding_preview = null;
    this.onboarding_preview_error = '';
    if (!entity_type_id) return;

    this.onboarding_preview_loading = true;
    try {
      const response = await prepareXVerdictOnboarding({
        entity_type_id,
        ...project_payload(this.project_id),
      });
      apply_onboarding_preview(this, response, entity_type_id);
    } catch (error) {
      apply_onboarding_error(this, error, entity_type_id);
    } finally {
      finish_onboarding_preview(this, entity_type_id);
    }
  },
  validate_case_creation() {
    const entity_type_id = to_number(this.new_case_form.entity_type_id);
    const entity_type = this.case_types.find((item) => Number(item?.id) === entity_type_id);
    const context = {
      entity_type_id,
      requester: normalize_text(this.new_case_form.requester),
      user_email: normalize_text(this.new_case_form.user_email),
      description: normalize_text(this.new_case_form.description),
      source_channel: normalize_text(this.new_case_form.source_channel),
      is_email_source: is_email_source(this.new_case_form.source_channel),
      domain_label: get_entity_type_name(entity_type) || 'Case',
    };
    return {
      ...context,
      error: CASE_VALIDATIONS.find((validation) => validation.invalid(context))?.error,
    };
  },
  build_case_payload(context) {
    return {
      entity_name: context.description || context.requester || `${context.domain_label} case`,
      entity_type_id: context.entity_type_id,
      form_ids: selected_form_ids(this),
      run_onboarding: true,
      source_channel: context.source_channel,
      user_email: context.user_email || undefined,
      send_user_email: context.is_email_source,
      ...project_payload(this.project_id),
    };
  },
  async submit_case(payload) {
    const response = await axios.post(
      CREATE_ENTITY_APPLICATION_ONLY_URL,
      payload,
      get_request_config()
    );
    return response?.data || {};
  },
  handle_case_response(create_data, entity_type_id) {
    const { entity_profile_id, application_id } = require_onboarding_ids(create_data);

    this.created_case = {
      application_id,
      entity_profile_id,
      entity_type_id,
      case_code: created_case_code(create_data, application_id),
    };
    this.onboarding_execution = [
      create_data?.onboarding_execution,
      create_data?.data?.onboarding_execution,
    ].find(Boolean) || null;
    this.onboarding_status = [
      create_data?.onboarding_status,
      create_data?.data?.onboarding_status,
    ].find(Boolean) || null;
    return application_id;
  },
  async refresh_created_case(application_id) {
    clear_entity_profile_list_cache();
    await this.load_company_cases();
    this.created_case = this.cases.find(
      (item) => Number(item.application_id) === Number(application_id)
    ) || this.created_case;
  },
  async create_new_case() {
    this.new_case_error = '';
    const context = this.validate_case_creation();
    if (context.error) {
      this.new_case_error = context.error;
      return;
    }

    this.submitting_new_case = true;
    let creation_succeeded = false;
    try {
      const create_data = await this.submit_case(this.build_case_payload(context));
      const application_id = this.handle_case_response(create_data, context.entity_type_id);
      await this.refresh_created_case(application_id);
      creation_succeeded = true;
    } catch (error) {
      this.new_case_error = creation_error_message(error);
      if (this.created_case) {
        await this.refresh_created_case(this.created_case.application_id);
      }
    } finally {
      this.submitting_new_case = false;
    }

    if (creation_succeeded) {
      const created_case = this.created_case;
      this.close_new_case_modal();
      if (created_case) this.go_to_case_detail(created_case);
    }
  },
  go_to_created_case() {
    if (this.created_case) this.go_to_case_detail(this.created_case);
  },
};
